import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { languages, setInterpolationDictionary } from "simplydo/core/i18n";
import { OpenAPI } from "simplydo/interfaces";
import {
  Button,
  Container,
  Divider,
  Form,
  Icon,
  Label,
  Message,
  Modal,
  Select,
  Table,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";
import util from "utils/utils";

import ConnectionsManager from "components/lib/ConnectionsManager";
import { FakeLink } from "components/lib/UI";

const isValidPhoneNumber = (number: string) =>
  import("libphonenumber-js")
    .then((lib) => lib.isValidNumber(number))
    .catch(() => true);

const formatNumber = (number: string) => {
  let formattedNumber = number;
  if (number.indexOf("07") === 0) {
    formattedNumber = formattedNumber.replace("07", "+447");
  }
  const regex = /[^0-9]/g;
  const cleanNumber = formattedNumber.replace(regex, "");
  "".slice();
  if (formattedNumber.startsWith("+")) {
    return `+${cleanNumber.slice(0, 2)} ${cleanNumber.slice(2)}`;
  }
  return cleanNumber;
};

export function UserSettingsAccount() {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const onSwitchOrganisation = useCallback(
    (org: OpenAPI.Schemas["Organisation"]) =>
      dispatch(actions.user.switchOrganisation(org)),
    [dispatch],
  );
  const onPhoneNumbersChanged = useCallback(
    (phoneNumbers: OpenAPI.Schemas["User"]["phoneNumbers"]) =>
      dispatch(actions.user.updatePhoneNumbers(phoneNumbers)),
    [dispatch],
  );
  const reloadUser = useUserReload();

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteAccountString, setDeleteAccountString] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [formattedNewNumber, setFormattedNewNumber] = useState("");
  const [verificationEntry, setVerificationEntry] = useState<{
    number: string;
  } | null>(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(
    null,
  );
  const [validNumber, setValidNumber] = useState(false);

  const phoneNumbers = useMemo(
    () => user?.phoneNumbers || [],
    [user?.phoneNumbers],
  );

  useEffect(() => {
    const newNumber = formatNumber(newPhoneNumber);
    setFormattedNewNumber(newNumber);
    isValidPhoneNumber(newNumber)
      .then((valid) => {
        setValidNumber(valid);
      })
      .catch(() => {
        setValidNumber(true);
      });
  }, [newPhoneNumber]);

  const addNumber = useCallback(() => {
    util
      .confirm(
        "Confirm phone number",
        `We are about to send a verification code to '${formattedNewNumber}'. Please make sure you entered it correctly.`,
      )
      .then(() => {
        api.auth.addPhoneNumber(
          formattedNewNumber,
          (newNumber) => {
            onPhoneNumbersChanged([...phoneNumbers, newNumber]);
            setNewPhoneNumber("");
            setVerificationEntry(newNumber);
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  }, [formattedNewNumber, onPhoneNumbersChanged, phoneNumbers]);

  const verify = useCallback(() => {
    if (!verificationEntry) {
      return;
    }
    setVerificationLoading(true);
    api.auth.verifyPhoneNumber(
      verificationEntry.number,
      verifyCode,
      () => {
        setVerificationLoading(false);
        setVerificationEntry(null);
        setVerifyCode("");
        toast.success("Phone number verified!");

        onPhoneNumbersChanged(
          phoneNumbers.map((pn) => {
            if (pn.number === verificationEntry.number) {
              return { number: pn.number, verified: true };
            }
            return pn;
          }),
        );
      },
      (e) => {
        setVerificationLoading(false);
        toast.error("Verification failed.");
        setVerificationError(e.message);
      },
    );
  }, [verificationEntry, verifyCode, onPhoneNumbersChanged, phoneNumbers]);

  const sendNewCode = useCallback(() => {
    if (!verificationEntry) {
      return;
    }
    util
      .confirm(
        "Generate new code",
        `We are about to send a new verification code to '${verificationEntry.number}'. This will invalidate the previous code. Proceed?`,
      )
      .then(() => {
        api.auth.resendPhoneNumberVerification(
          verificationEntry.number,
          () => {
            toast.success("Sent new verification token.");
          },
          () => {
            toast.error(
              "Failed to generate new verification token, please contact an administrator.",
            );
          },
        );
      })
      .catch(() => {});
  }, [verificationEntry]);

  const deleteAccount = () => {
    if (!user?._id) return;
    if (deleteAccountString === "DELETE") {
      util
        .confirm(
          t("users.account.options.delete.confirm.title"),
          t("users.account.options.delete.confirm.info"),
        )
        .then(() => {
          setDeletingAccount(true);
          api.users.delete(
            user._id,
            {},
            () => {
              toast(t("users.account.options.delete.confirm.success"));
              setErrorMessage("");
              setDeletingAccount(false);

              api.token = null;
              api.options?.onLogout?.();

              navigate("/");
            },
            (err) => {
              setErrorMessage(err.message);
              setDeletingAccount(false);
            },
          );
        })
        .catch(() => {});
    } else {
      setErrorMessage('Please type "DELETE" to delete your account');
      setDeletingAccount(false);
    }
  };

  const switchOrganisation = (org: OpenAPI.Schemas["Organisation"]) => {
    if (!user?._id) return;
    api.users.switchOrganisation(
      user._id,
      org._id,
      (data) => {
        onSwitchOrganisation(data.organisation);
      },
      (err) => toast.error(err.message),
    );
  };

  const leaveOrganisation = (org: OpenAPI.Schemas["Organisation"]) => {
    util
      .confirm(
        "Really leave this organisation?",
        "You may not be able to re-join automatically.",
      )
      .then(() => {
        if (!user?._id) return;
        api.users.leaveOrganisation(
          user._id,
          org._id,
          () => {},
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  };

  const changeLanguage = (language: string) => {
    if (!user?._id) return;
    const currentLanguage = i18n.language;
    i18n.changeLanguage(language);
    setInterpolationDictionary(user?.ownerOrganisation?.terminology);
    api.users.updateLanguagePreferences(
      user._id,
      language,
      () => {},
      (err) => {
        i18n.changeLanguage(currentLanguage);
        setInterpolationDictionary(user?.ownerOrganisation?.terminology);
        toast.error(err.message);
      },
    );
  };

  if (!user) return null;

  return (
    <Container>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={!!verificationEntry}
      >
        <Modal.Header>
          <Icon name="lock" style={{ paddingRight: "5px" }} />
          Verify phone number
          <br />
        </Modal.Header>
        <Modal.Content>
          <p>
            To complete the verification process, please enter the six digit
            one-time code which was sent to &quot;{verificationEntry?.number}
            &quot;.
          </p>
          <p>
            Please be aware that verification codes are only valid for one hour.
            If you missed yours or if it expired please{" "}
            <a style={{ cursor: "pointer" }} href="#a" onClick={sendNewCode}>
              click here
            </a>{" "}
            to receive a new code for this number.
          </p>
          {verificationError ? (
            <Message error>{verificationError}</Message>
          ) : null}
          <div style={{ textAlign: "center" }}>
            <Input
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.keyCode === 13) verify();
              }}
              onChange={(e) => setVerifyCode(e.target.value)}
              value={verifyCode}
              placeholder="123456"
              size="large"
              autoFocus
            />
          </div>
          <div style={{ textAlign: "center", marginTop: 5 }}>
            <FakeLink withBorder onClick={() => sendNewCode()}>
              Resend verification code
            </FakeLink>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            content={t("generic.cancel")}
            onClick={() => {
              setVerificationEntry(null);
              setVerifyCode("");
            }}
          />
          <Button
            primary
            content="Continue"
            onClick={() => verify()}
            loading={verificationLoading}
            disabled={verifyCode.length !== 6}
          />
        </Modal.Actions>
      </Modal>

      <h3>{t("users.account.options.orgs.title")}</h3>
      <p>{t("users.account.options.orgs.info", { appName: util.appName() })}</p>
      <Table basic="very">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              {t("users.account.options.orgs.list.name")}
            </Table.HeaderCell>
            <Table.HeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {user.ownerOrganisations?.map((o) => (
            <Table.Row key={o._id}>
              <Table.Cell>{o.name}</Table.Cell>
              <Table.Cell collapsing textAlign="right">
                {o._id === user.ownerOrganisation?._id ? (
                  <Label size="tiny" color="green">
                    {t("users.account.options.orgs.list.primary")}
                  </Label>
                ) : (
                  <>
                    <Button
                      basic
                      size="mini"
                      content={t("users.account.options.orgs.list.makePrimary")}
                      onClick={() => switchOrganisation(o)}
                    />
                    <Button
                      basic
                      color="orange"
                      size="mini"
                      content="Leave"
                      onClick={() => leaveOrganisation(o)}
                    />
                  </>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <Message size="tiny">{t("users.account.options.orgs.message")}</Message>
      <Divider />

      <h3>{t("users.preferences.language.title")}</h3>
      <p>{t("users.preferences.language.info")}</p>
      <Select
        fluid
        value={i18n.language}
        options={languages.map((l) => ({
          flag: l.flag,
          key: l.key,
          value: l.key,
          text: l.name,
        }))}
        onChange={(_e, s) => changeLanguage(s.value as string)}
      />
      <Divider />

      <h3>{t("users.profile.communication.phone.title")}</h3>
      <p>{t("users.profile.communication.phone.info")}</p>
      <Table fixed basic="very" padded>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              {t("users.profile.communication.phone.table.number")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("users.profile.communication.phone.table.verification")}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="right"></Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {phoneNumbers.map((entry) => (
            <Table.Row key={entry.number}>
              <Table.Cell>{entry.number}</Table.Cell>
              <Table.Cell>
                {entry.verified ? (
                  <>
                    <Icon name="check circle" style={{ paddingRight: 5 }} />
                    Verified
                  </>
                ) : (
                  <>
                    <Icon name="close" style={{ paddingRight: 5 }} />
                    Not verified
                    <Button
                      style={{ marginLeft: 10 }}
                      compact
                      size="small"
                      basic
                      content={"Verify now"}
                      onClick={() => {
                        setVerificationEntry(entry);
                      }}
                    />
                  </>
                )}
              </Table.Cell>
              <Table.Cell textAlign="right">
                <Button
                  compact
                  size="small"
                  basic
                  color="red"
                  content={t("generic.remove")}
                  onClick={() => {
                    util
                      .confirm(
                        "Remove number",
                        `Are you sure you want to remove '${formatNumber(entry.number)}'?`,
                      )
                      .then(() => {
                        api.auth.removePhoneNumber(
                          entry.number,
                          () => {
                            onPhoneNumbersChanged(
                              phoneNumbers.filter(
                                (pn) => pn.number !== entry.number,
                              ),
                            );
                          },
                          (err) => toast.error(err.message),
                        );
                      })
                      .catch(() => {});
                  }}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <Form>
        <Form.Field
          control={Input}
          type="text"
          placeholder={t("users.profile.communication.phone.table.info2")}
          value={newPhoneNumber}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setNewPhoneNumber(event.target.value);
          }}
          error={
            newPhoneNumber === "" || validNumber
              ? null
              : {
                  content: t(
                    "users.profile.communication.phone.table.placeholder",
                  ),
                  pointing: "above",
                }
          }
          action={{
            disabled: !validNumber,
            content: "Add number",
            icon: "angle double right",
            labelPosition: "right",
            onClick: () => addNumber(),
          }}
        />
      </Form>

      {user?.ownerOrganisation?.enabledFeatures?.includes(
        "teamsHandleAdvertising",
      ) ? (
        <>
          <Divider section />

          <ConnectionsManager
            entity="user"
            entityId={user._id}
            serviceProvider="microsoft"
            service="microsoftTeams"
            contextId="userIntegration"
            title="Microsoft Teams"
            subtitle="Connect your Microsoft Teams account to advertise your Teams handle. Once connected, other users will be able to message you directly via Teams."
            connectionButtonText="Connect Microsoft Teams"
            onDisconnect={() => {
              reloadUser();
            }}
          />
          <Message>
            Please note that your email address forms part of your Teams handle
            link and may be viewable to others.
          </Message>
        </>
      ) : null}

      <Divider section />
      <ConnectionsManager
        entity="user"
        entityId={user._id}
        serviceProvider="microsoft"
        service="office365"
        contextId="userIntegration"
        title="Microsoft Office365"
        subtitle="Connect your Microsoft account to let you attach files from your OneDrive when uploading content in the platform."
        connectionButtonText="Connect Microsoft Office365"
      />

      <Divider section />
      <h3>Data protection complaints </h3>
      <p>
        If you would like to make a data protection complaint to SimplyDo,
        please tap the button below to find out more about how you can do so.
      </p>
      <Button
        style={{ maxWidth: "fit-content" }}
        content="Make a complaint"
        as={Link}
        target="_blank"
        to="https://support.simplydo.co.uk/hc/simplydo-help-center/articles/1776680509-data-protection-complaints"
      />

      {!user.ownerOrganisation?.disableAccountSelfDeletion ? (
        <>
          <Divider />
          <h3>{t("users.account.options.delete.title")}</h3>
          <p>
            {t("users.account.options.delete.info", {
              appName: util.appName(),
            })}
          </p>
          <Label color="red" ribbon>
            <Icon name="exclamation triangle" />{" "}
            {t("users.account.options.delete.warningLabel")}
          </Label>
          <Message color="orange">
            {t("users.account.options.delete.warningMessage")}
          </Message>
          <p>{t("users.account.options.delete.enterPassword")}</p>
          {errorMessage && !deletingAccount && (
            <Message size="small" negative>
              <Icon name="warning circle" />
              {errorMessage}.
            </Message>
          )}
          <Input
            size="small"
            fluid
            value={deleteAccountString}
            action={
              <Button
                color="red"
                content={t("users.account.options.delete.confirm.title")}
                onClick={deleteAccount}
              />
            }
            placeholder="DELETE"
            onChange={(e) => setDeleteAccountString(e.target.value)}
          />
        </>
      ) : null}
    </Container>
  );
}

export default UserSettingsAccount;
